import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { watermarkImageWithText } from "utils/waterMark";
import "../detailTransaction/i18n";
import "./DeliveryEvidence.scss";
import { GlobalLoader } from "@pickit/pickit-components";

export type DeliveryEvidenceItem = { type: string; value: string };

export type WatermarkMeta = {
  latitude?: number;
  longitude?: number;
  finishByAdmin?: boolean;
  timestamp?: Date;
};

export type DeliveryEvidenceProps = {
  items: DeliveryEvidenceItem[];
  loading?: boolean;
  watermarkMeta?: WatermarkMeta;
};

const EvidenceImage: React.FC<{
  item: DeliveryEvidenceItem;
  watermarkText: string;
  hasCoords?: boolean;
  timestamp?: Date;
}> = ({ item, watermarkText, hasCoords = true, timestamp }) => {
  const [, setError] = useState(false);
  const [watermarkedSrc, setWatermarkedSrc] = useState<string | null>(null);
  const proofType = item.type.toUpperCase() === "SIGN" ? "sign" : "photo";

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const showBox = proofType === "photo" || hasCoords;
        const dataUrl = watermarkImageWithText(
          img,
          watermarkText,
          proofType,
          showBox,
          timestamp
        );
        setWatermarkedSrc(dataUrl);
      } catch {
        setWatermarkedSrc(item.value);
      }
    };
    img.onerror = () => setError(true);
    img.src = item.value;
  }, [item.value, watermarkText, proofType, hasCoords, timestamp]);

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      src={watermarkedSrc ?? ""}
      className="delivery-evidence-img"
      onError={() => setError(true)}
    />
  );
};

const DeliveryEvidence: React.FC<DeliveryEvidenceProps> = ({
  items,
  loading = false,
  watermarkMeta = {},
}): JSX.Element => {
  const { t } = useTranslation();

  const showCoords = !watermarkMeta.finishByAdmin && watermarkMeta.latitude != null && watermarkMeta.longitude != null;
  const lat = watermarkMeta.latitude ?? "-";
  const lng = watermarkMeta.longitude ?? "-";
  const watermarkText = [
    `${t("detailTransaction:lat")} ${lat}\n${t("detailTransaction:long")} ${lng}`,
    t("detailTransaction:confidetialMenssage"),
  ]
    .filter(Boolean)
    .join("\n");

  const imageItems = items
    .filter(
      (item) =>
        item.value &&
        (item.type === "PHOTO" || item.type === "SIGN")
    )
    .sort((a, b) => (a.type === "SIGN" && b.type === "PHOTO" ? -1 : a.type === "PHOTO" && b.type === "SIGN" ? 1 : 0));

  return (
    <>
      <GlobalLoader
      isLoading={loading}
    />
    <div className="delivery-evidence-page delivery-evidence-page--bare">
        <div className="delivery-evidence-grid delivery-evidence-grid--side-by-side">
          {imageItems.map((item, index) => (
            <EvidenceImage
              key={`${item.type}-${index}`}
              item={item}
              watermarkText={watermarkText}
              hasCoords={showCoords}
              timestamp={watermarkMeta.timestamp} />
          ))}
        </div>
      </div></>
  );
};

export default DeliveryEvidence;
